import { useEffect } from "react";
import { useCommon } from "../context/CommonContext";
import { BASE_URL, logger } from "../lib/utils";

// 🧩 Helpers
const upsertMeta = (selector, attributes) => {
	let element = document.head.querySelector(selector);

	if (!element) {
		element = document.createElement("meta");
		element.setAttribute("data-dynamic", "true");
		document.head.appendChild(element);
	}

	Object.entries(attributes).forEach(([key, value]) => {
		if (value !== undefined && value !== null && value !== "") {
			element.setAttribute(key, value);
		}
	});
};

const upsertLink = (selector, attributes) => {
	let element = document.head.querySelector(selector);

	if (!element) {
		element = document.createElement("link");
		element.setAttribute("data-dynamic", "true");
		document.head.appendChild(element);
	}

	Object.entries(attributes).forEach(([key, value]) => {
		if (value !== undefined && value !== null && value !== "") {
			element.setAttribute(key, value);
		}
	});
};

const upsertScript = (id, json) => {
	let script = document.getElementById(id);

	if (!script) {
		script = document.createElement("script");
		script.type = "application/ld+json";
		script.id = id;
		script.setAttribute("data-dynamic", "true");
		document.head.appendChild(script);
	}

	script.textContent = JSON.stringify(json);
};

const slugify = (text = "") =>
	text.toLowerCase().replace(/\s+/g, "-");

const SeoHead = ({
	pageTitle,
	description,
	image,
	path = "/",
	type = "website",
	game = null,
}) => {
	const { settings } = useCommon();

	useEffect(() => {
		if (!settings) return;
		document
			.querySelectorAll('[data-dynamic="true"]')
			.forEach((el) => el.remove());

		const base = settings.canonicalBaseUrl || BASE_URL;

		const canonical = base
			? new URL(path, base).toString()
			: "";

		const title =
			pageTitle && settings.titleTemplate?.includes("%s")
				? settings.titleTemplate.replace("%s", pageTitle)
				: pageTitle || settings.defaultTitle;

		const metaDescription =
			description || settings.defaultDescription || "";

		const robots = [
			settings.robots?.index === false ? "noindex" : "index",
			settings.robots?.follow === false ? "nofollow" : "follow",
		].join(",");

		const ogImage = image || settings.ogImage || "";

		const keywords =
			game?.tags?.slice(0, 10).join(", ") ||
			(Array.isArray(settings.defaultKeywords)
				? settings.defaultKeywords.join(", ")
				: "");

		logger.debug("SEO Canonical:", canonical);

		document.title = title;

		upsertMeta('meta[name="description"]', {
			name: "description",
			content: metaDescription,
		});

		upsertMeta('meta[name="keywords"]', {
			name: "keywords",
			content: keywords,
		});

		upsertMeta('meta[name="robots"]', {
			name: "robots",
			content: robots,
		});

		upsertMeta('meta[property="og:type"]', {
			property: "og:type",
			content: type === "game" ? "video.game" : "website",
		});

		upsertMeta('meta[property="og:site_name"]', {
			property: "og:site_name",
			content: settings.siteName || "CoffeeGamesOnline",
		});

		upsertMeta('meta[property="og:title"]', {
			property: "og:title",
			content: title,
		});

		upsertMeta('meta[property="og:description"]', {
			property: "og:description",
			content: metaDescription,
		});

		if (canonical) {
			upsertMeta('meta[property="og:url"]', {
				property: "og:url",
				content: canonical,
			});

			upsertLink('link[rel="canonical"]', {
				rel: "canonical",
				href: canonical,
			});

			upsertLink('link[rel="alternate"]', {
				rel: "alternate",
				hrefLang: "en",
				href: canonical,
			});
		}

		if (ogImage) {
			upsertMeta('meta[property="og:image"]', {
				property: "og:image",
				content: ogImage,
			});

			upsertMeta('meta[property="og:image:alt"]', {
				property: "og:image:alt",
				content: game?.title || title,
			});

			upsertMeta('meta[name="twitter:image"]', {
				name: "twitter:image",
				content: ogImage,
			});
		}

		upsertMeta('meta[name="twitter:card"]', {
			name: "twitter:card",
			content: "summary_large_image",
		});

		upsertMeta('meta[name="twitter:title"]', {
			name: "twitter:title",
			content: title,
		});

		upsertMeta('meta[name="twitter:description"]', {
			name: "twitter:description",
			content: metaDescription,
		});

		if (settings.twitterHandle) {
			upsertMeta('meta[name="twitter:site"]', {
				name: "twitter:site",
				content: settings.twitterHandle,
			});
		}

		if (settings.verification?.google) {
			upsertMeta('meta[name="google-site-verification"]', {
				name: "google-site-verification",
				content: settings.verification.google,
			});
		}

		if (settings.verification?.bing) {
			upsertMeta('meta[name="msvalidate.01"]', {
				name: "msvalidate.01",
				content: settings.verification.bing,
			});
		}
		if (game?.buildUrl) {
			upsertLink('link[rel="preload"]', {
				rel: "preload",
				as: "script",
				href: game.buildUrl,
				crossOrigin: "anonymous",
			});
		}

		if (canonical) {
			const segments = path.split("/").filter(Boolean);

			const breadcrumbs = [
				{
					"@type": "ListItem",
					position: 1,
					name: "Home",
					item: base,
					"@id": base,
				},
			];

			let currentPath = "";

			segments.forEach((seg, i) => {
				currentPath += `/${seg}`;

				let name = seg
					.replace(/-/g, " ")
					.replace(/\b\w/g, (c) => c.toUpperCase());

				if (game) {
					if (seg === "game") name = "Games";
					if (seg === game.slug) name = game.title;
					if (seg === slugify(game.genre)) name = game.genre;
				}

				const url = `${base}${currentPath}`;

				breadcrumbs.push({
					"@type": "ListItem",
					position: i + 2,
					name,
					item: url,
					"@id": url,
				});
			});

			upsertScript("breadcrumb-schema", {
				"@context": "https://schema.org",
				"@type": "BreadcrumbList",
				itemListElement: breadcrumbs,
			});
		}

		if (type === "game" && game && canonical) {
			const gameSchema = {
				"@context": "https://schema.org",
				"@type": "VideoGame",
				name: game.title,
				description: metaDescription,
				image: ogImage,
				url: canonical,
				genre: game.genre,
				applicationCategory: "Game",
				operatingSystem: "Browser",
				inLanguage: "en",
				isAccessibleForFree: true,
				datePublished: game.createdAt,

				author: {
					"@type": "Organization",
					name: settings.siteName || "CoffeeGamesOnline",
				},

				publisher: {
					"@type": "Organization",
					name: settings.siteName || "CoffeeGamesOnline",
				},

				potentialAction: {
					"@type": "PlayAction",
					target: canonical,
				},

				keywords: game.tags?.join(", "),

				...(game.rating && {
					aggregateRating: {
						"@type": "AggregateRating",
						ratingValue: game.rating,
						ratingCount: game.ratingCount || 1,
					},
				}),

				...(game.playCount && {
					interactionStatistic: {
						"@type": "InteractionCounter",
						interactionType: { "@type": "PlayAction" },
						userInteractionCount: game.playCount,
					},
				}),
			};

			upsertScript("game-schema", gameSchema);
		}

		if (type === "website") {
			upsertScript("website-schema", {
				"@context": "https://schema.org",
				"@type": "WebSite",
				name: settings.siteName,
				url: base,
				potentialAction: {
					"@type": "SearchAction",
					target: `${base}/search?q={search_term_string}`,
					"query-input": "required name=search_term_string",
				},
			});
		}
	}, [settings, pageTitle, description, image, path, game, type]);

	return null;
};

export default SeoHead;